import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  AgentConversationSessionSummary,
  IAgentConversationListResponse,
} from '../types/agent-conversation.types';
import type { ConversationResponse } from 'services/conversation-services';

/** Conversation ids deleted in this tab — skip realtime duplicate toasts. */
const locallyDeletedConversationIds = new Set<string>();
const LOCAL_DELETE_TOAST_SUPPRESS_MS = 10_000;

export function markConversationDeletedLocally(conversationId: string) {
  locallyDeletedConversationIds.add(conversationId);
  // Keep the mark briefly so late/duplicate CONVERSATION_DELETED events don't toast again.
  window.setTimeout(() => {
    locallyDeletedConversationIds.delete(conversationId);
  }, LOCAL_DELETE_TOAST_SUPPRESS_MS);
}

export function clearLocallyDeletedConversation(conversationId: string) {
  locallyDeletedConversationIds.delete(conversationId);
}

/** True when this tab initiated the delete (and already showed success). */
export function wasConversationDeletedLocally(conversationId: string): boolean {
  return locallyDeletedConversationIds.has(conversationId);
}

/** @deprecated Prefer wasConversationDeletedLocally — kept for call-site compatibility. */
export function consumeLocallyDeletedConversation(conversationId: string): boolean {
  return wasConversationDeletedLocally(conversationId);
}

/** Remove a session from all cached agent-conversation-list queries. */
export function removeAgentConversationFromCache(
  queryClient: QueryClient,
  sessionId: string,
) {
  queryClient.setQueriesData<InfiniteData<IAgentConversationListResponse>>(
    { queryKey: ['agent-conversation-list'] },
    (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      let removed = false;
      const pages = oldData.pages.map((page) => {
        const sessions = page.sessions.filter((session) => {
          if (session.session_id === sessionId) {
            removed = true;
            return false;
          }
          return true;
        });

        if (sessions.length === page.sessions.length) return page;

        return {
          ...page,
          sessions,
          total_count: Math.max(0, (page.total_count ?? page.sessions.length) - 1),
        };
      });

      return removed ? { ...oldData, pages } : oldData;
    },
  );
}

/** Optimistically prepend a new session to the agent sidebar history cache. */
export function prependAgentConversationToCache(
  queryClient: QueryClient,
  agentId: string,
  projectKey: string,
  sessionId: string,
) {
  queryClient.setQueryData<InfiniteData<IAgentConversationListResponse>>(
    ['agent-conversation-list', { agent_id: agentId, project_key: projectKey }],
    (oldData) => {
      if (!oldData?.pages?.length) return oldData;

      const now = new Date().toISOString();
      // Runtime list shape is a partial of AgentConversationSessionSummary (see
      // agent-conversation-service). Cast to keep the cache updater typed.
      const newSession = {
        agent_id: agentId,
        widget_id: '',
        session_id: sessionId,
        created_at: now,
        last_entry_date: now,
        total_count: (oldData.pages[0]?.total_count ?? 0) + 1,
        conversation: {
          SessionId: sessionId,
          Title: '',
          Query: '',
          ConversationType: 'chat',
        },
      } as unknown as AgentConversationSessionSummary;

      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => {
          if (index !== 0) return page;

          const sessions = page.sessions.filter((session) => session.session_id !== sessionId);
          return {
            ...page,
            sessions: [newSession, ...sessions],
            total_count: (page.total_count ?? sessions.length) + 1,
          };
        }),
      };
    },
  );
}

export function invalidateAgentConversationList(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['agent-conversation-list'] });
}

/** Keep conversation-by-id query cache in sync after a manual rename. */
export function updateConversationTitleInQueryCache(
  queryClient: QueryClient,
  conversationId: string,
  newTitle: string,
) {
  queryClient.setQueryData<ConversationResponse>(['conversation', conversationId], (old) =>
    old ? { ...old, conversationTitle: newTitle } : old,
  );
}

/** Sync React Query caches after renaming a conversation (header + agent sidebar). */
export function syncConversationRenameCaches(
  queryClient: QueryClient,
  conversationId: string,
  newTitle: string,
) {
  updateConversationTitleInQueryCache(queryClient, conversationId, newTitle);
  invalidateAgentConversationList(queryClient);
}
